import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

// Define TypeScript interfaces based on the API response structure
export interface Webhook {
  _id: string;
  webhook_method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  is_active: boolean;
  is_deleted: boolean;
  webhook_name: string;
  webhook_description: string;
  webhook_trigger_events: string[];
  webhook_url: string;
  webhook_api_key_format: 'HEADER' | 'QUERY' | 'BODY';
  webhook_api_key: string;
  webhook_api_value: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface CreateWebhookData {
  webhook_name: string;
  webhook_description: string;
  webhook_trigger_events: string[];
  webhook_method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  webhook_url: string;
  webhook_api_key_format: 'HEADER' | 'QUERY' | 'BODY';
  webhook_api_key: string;
  webhook_api_value: string;
  is_active: boolean;
}

export interface UpdateWebhookData extends CreateWebhookData { }

export interface WebhookListParams {
  skip: number;
  limit: number;
  webhook_name?: string;
  webhook_trigger_event?: string;
  is_active?: boolean;
}

export interface WebhookListResponse {
  success: boolean;
  code: number;
  data: {
    list: Webhook[];
    totalCount: number;
    last_modified: string;
  };
  headers: Record<string, any>;
}

export interface WebhookResponse {
  success: boolean;
  code: number;
  data: Webhook;
  headers: Record<string, any>;
}

export interface WebhookStats {
  total_webhooks: number;
  active_webhooks: number;
  inactive_webhooks: number;
  total_deliveries: number;
}

export interface TriggerEventOption {
  label: string;
  value: string;
}

export interface TriggerEventDropdownResponse {
  success: boolean;
  code: number;
  data: TriggerEventOption[];
}

export interface WebhookLog {
  _id: string;
  id: string;
  webhook_id: string;
  webhook_config: {
    _id: string;
    webhook_name: string;
    webhook_description: string;
    webhook_method: string;
    webhook_url: string;
    webhook_api_key_format: string;
    webhook_api_key: string;
    webhook_api_value: string;
    webhook_trigger_events: string[];
    is_active: boolean;
    is_deleted: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
    id: string;
  };
  trigger_type: string;
  payload: any;
  response?: {
    output?: any;
  };
  status: 'SUCCESS' | 'FAILED' | 'PENDING' | 'RETRY';
  error_message?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface WebhookLogsParams {
  skip: number;
  limit: number;
  webhook_config_id: string;
  status?: 'SUCCESS' | 'FAILED' | 'PENDING' | 'RETRY';
  start_date?: string;
  end_date?: string;
}

export interface WebhookLogsResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    items: WebhookLog[];
  };
}

// Webhook trigger events enum (based on common webhook events)
export const WEBHOOK_TRIGGER_EVENTS = {
  ON_FILE_COMPARE_START: 'ON_FILE_COMPARE_START',
  ON_FILE_COMPARE_COMPLETE: 'ON_FILE_COMPARE_COMPLETE',
  ON_FILE_UPLOAD: 'ON_FILE_UPLOAD',
  ON_FILE_DELETE: 'ON_FILE_DELETE',
  ON_DOCUMENT_PROCESSED: 'ON_DOCUMENT_PROCESSED',
  ON_USER_CREATED: 'ON_USER_CREATED',
  ON_USER_UPDATED: 'ON_USER_UPDATED',
} as const;

export const WEBHOOK_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export const API_KEY_FORMATS = ['HEADER', 'QUERY', 'BODY'] as const;

class WebhookService {
  // GET: Fetch list with pagination and filters
  async getWebhooks(params: WebhookListParams): Promise<WebhookListResponse> {
    try {
      const response = await apiClient.post('/dms-v2-srv/webhook-config-srv/utils/list', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching webhooks:', error);
      throw error;
    }
  }

  // GET: Fetch single webhook by ID
  async getWebhookById(id: string): Promise<Webhook> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.WEBHOOKS.GET_BY_ID(id));
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching webhook ${id}:`, error);
      throw error;
    }
  }

  // POST: Create new webhook
  async createWebhook(webhookData: CreateWebhookData): Promise<Webhook> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.WEBHOOKS.CREATE, webhookData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating webhook:', error);
      throw error;
    }
  }

  // PUT: Update existing webhook
  async updateWebhook(id: string, webhookData: UpdateWebhookData): Promise<Webhook> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.WEBHOOKS.UPDATE(id), webhookData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating webhook ${id}:`, error);
      throw error;
    }
  }

  // DELETE: Remove webhook
  async deleteWebhook(id: string): Promise<void> {
    try {
      await apiClient.delete(API_ENDPOINTS.WEBHOOKS.DELETE(id));
    } catch (error) {
      console.error(`Error deleting webhook ${id}:`, error);
      throw error;
    }
  }

  // GET: Fetch trigger event dropdown options
  async getTriggerEventOptions(): Promise<TriggerEventOption[]> {
    try {
      const response = await apiClient.get('/dms-v2-srv/webhook-config-srv/utils/trigger-type-dropdown');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching trigger event options:', error);
      // Return fallback options based on the old enum
      return Object.entries(WEBHOOK_TRIGGER_EVENTS).map(([key, value]) => ({
        label: key.replace(/_/g, ' ').replace(/^ON /, '').toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        value
      }));
    }
  }

  // GET: Fetch webhook statistics (if endpoint exists)
  async getWebhookStats(): Promise<WebhookStats> {
    try {
      // This endpoint might not exist yet, so we'll calculate stats from the list
      const response = await this.getWebhooks({ skip: 0, limit: 1000 });
      const webhooks = response.data.list;

      const stats: WebhookStats = {
        total_webhooks: webhooks.length,
        active_webhooks: webhooks.filter(w => w.is_active).length,
        inactive_webhooks: webhooks.filter(w => !w.is_active).length,
        total_deliveries: 0, // This would need a separate endpoint for actual delivery stats
      };

      return stats;
    } catch (error) {
      console.error('Error fetching webhook stats:', error);
      // Return default stats on error
      return {
        total_webhooks: 0,
        active_webhooks: 0,
        inactive_webhooks: 0,
        total_deliveries: 0,
      };
    }
  }

  // POST: Fetch webhook logs/history
  async getWebhookLogs(params: WebhookLogsParams): Promise<WebhookLogsResponse> {
    try {
      const response = await apiClient.post('/dms-v2-srv/webhook-trigger-executor-srv/utils/history', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
      throw error;
    }
  }


  // Helper function to get available trigger events
  getAvailableTriggerEvents(): Array<{ value: string; label: string }> {
    return Object.entries(WEBHOOK_TRIGGER_EVENTS).map(([key, value]) => ({
      value,
      label: key.replace(/_/g, ' ').replace(/^ON /, '').toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }));
  }

  // Helper function to get available HTTP methods
  getAvailableMethods(): Array<{ value: string; label: string }> {
    return WEBHOOK_METHODS.map(method => ({
      value: method,
      label: method
    }));
  }

  // Helper function to get available API key formats
  getAvailableApiKeyFormats(): Array<{ value: string; label: string }> {
    return API_KEY_FORMATS.map(format => ({
      value: format,
      label: format.charAt(0) + format.slice(1).toLowerCase()
    }));
  }
}

export const webhooksService = new WebhookService();
export default webhooksService;